import TransparentLoader from '@components/core/transparent-loader';
import useRefreshAccounts from '@features/accounts/hooks/use-refresh-accounts';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import AuthErrorPage from '@src/components/auth-error-page';
import FullPageLoader from '@src/components/core/full-page-loader/full-page-loader';
import { SessionTimeoutCountdown } from '@src/features/auth/components/session-timeout-countdown';
import { useGetCobrand } from '@src/features/navigation/api/get-cobrand';
import { useLoader } from '@src/providers/loader/loader-context';
import { useRefreshAccountJobsStore } from '@src/stores';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Outlet } from '@tanstack/react-router';
import React, { Suspense, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

const TanStackRouterDevtools =
  process.env.NODE_ENV === 'production'
    ? () => null // Render nothing in production
    : React.lazy(() =>
        // Lazy load in development
        import('@tanstack/react-router-devtools').then((res) => ({
          default: res.TanStackRouterDevtools,
          // For Embedded Mode
          // default: res.TanStackRouterDevtoolsPanel
        })),
      );

const StyledContainerBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
}));

export default function RootLayout() {
  const { mutate } = useRefreshAccounts();
  const {
    state: { isLoading, message },
  } = useLoader();
  const { data: cobrandData } = useGetCobrand();

  /*~~~~~~~~~~~~~~~~~~~~~~~~~~~ START Account Refresh Logic~~~~~~~~~~~~~~~~~~~~~~~~~~~*/

  // Check if there are any refresh account jobs in the store
  const refreshAccountJobs =
    useRefreshAccountJobsStore.use.refreshAccountJobs();
  const { updateRefreshAccountJobStatus } =
    useRefreshAccountJobsStore.use.actions();
  // If there are refresh jobs, iterate over them and start the refresh process if status is 'init'
  useEffect(() => {
    Object.values(refreshAccountJobs)?.forEach((job) => {
      /* Job status must start as init and immediately change to pending before
     mutation to avoid infinite mutations. */
      if (job.status === 'init') {
        updateRefreshAccountJobStatus({
          accountId: job.accountId,
          status: 'pending',
        });
        mutate(job.accountId);
      }
    });
  }, [refreshAccountJobs, updateRefreshAccountJobStatus, mutate]);

  /*~~~~~~~~~~~~~~~~~~~~~~~~~~~ END Account Refresh Logic~~~~~~~~~~~~~~~~~~~~~~~~~~~*/

  return (
    <StyledContainerBox className='root-container'>
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary
            onReset={reset}
            fallbackRender={({ resetErrorBoundary }) => (
              <AuthErrorPage resetErrorBoundary={resetErrorBoundary} />
            )}
          >
            <Suspense
              fallback={<FullPageLoader cobrandImage={cobrandData?.image} />}
            >
              <TransparentLoader isLoading={isLoading} message={message} />
              <SessionTimeoutCountdown />
              <Outlet />
              <ReactQueryDevtools />
              <TanStackRouterDevtools />
            </Suspense>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
    </StyledContainerBox>
  );
}
